import { useCallback } from "react";
import { useAppDispatch, useAppSelector } from "redux/hooks";
import { setRequesterInPersonConsented } from "redux/appSlice";
import { RootState } from "utils/store";
import {
  fetchRequesterConsent,
  recordRequesterConsent,
} from "./InPersonSigningService";

/** Shared across every mounted signer row so concurrent callers make one GET. */
let inFlightFetch: Promise<boolean> | null = null;

/**
 * The requester's own in-person attestation is one-time and account-level —
 * unlike the host's and the signer's, which are per envelope. This caches the
 * answer in redux for the session so the compose-time prompt is only ever
 * asked the first time an in-person signer is added, never again after.
 */
export const useRequesterInPersonConsent = () => {
  const dispatch = useAppDispatch();
  const consented = useAppSelector(
    (state: RootState) => state.app.requesterInPersonConsented,
  );

  /** Resolves the current consent state, fetching it at most once per session. */
  const ensureConsentStatus = useCallback(async (): Promise<boolean> => {
    if (consented != null) return consented;
    if (!inFlightFetch) {
      inFlightFetch = fetchRequesterConsent()
        .then((status) => status.requesterConsented)
        .catch(() => false)
        .finally(() => {
          inFlightFetch = null;
        });
    }
    const result = await inFlightFetch;
    dispatch(setRequesterInPersonConsented(result));
    return result;
  }, [consented, dispatch]);

  /** Posts the attestation once and caches it so no other row asks again. */
  const recordConsent = useCallback(async () => {
    await recordRequesterConsent();
    dispatch(setRequesterInPersonConsented(true));
  }, [dispatch]);

  return { consented, ensureConsentStatus, recordConsent };
};
